const API_BASE = process.env.REACT_APP_API_URL || '/api';

function getToken() {
  return localStorage.getItem('subsdata_token');
}

async function request(path, options = {}) {
  const token = getToken();
  const headers = { ...options.headers };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
    options.body = JSON.stringify(options.body);
  }

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  // Handle PDF responses
  if (res.headers.get('content-type')?.includes('application/pdf')) {
    const blob = await res.blob();
    return { blob, headers: res.headers };
  }

  // Handle CSV responses
  if (res.headers.get('content-type')?.includes('text/csv')) {
    const blob = await res.blob();
    return { blob, headers: res.headers };
  }

  if (!res.ok) {
    const data = await res.json().catch(() => ({ error: 'Request failed' }));
    const err = new Error(data.error || 'Request failed');
    err.status = res.status;
    throw err;
  }

  return res.json();
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const api = {
  // Auth
  login: (username, password) =>
    request('/auth/login', { method: 'POST', body: { username, password } }),
  changePassword: (current_password, new_password) =>
    request('/auth/change-password', { method: 'POST', body: { current_password, new_password } }),

  // Dashboard
  getDashboard: () => request('/dashboard'),

  // Titles
  getTitles: () => request('/titles'),
  getTitle: (id) => request(`/titles/${id}`),
  createTitle: (data) => request('/titles', { method: 'POST', body: data }),
  updateTitle: (id, data) => request(`/titles/${id}`, { method: 'PUT', body: data }),
  deleteTitle: (id) => request(`/titles/${id}`, { method: 'DELETE' }),
  uploadTitleLogo: (titleId, file) => {
    const formData = new FormData();
    formData.append('logo', file);
    return request(`/titles/${titleId}/logo`, { method: 'POST', body: formData });
  },
  deleteTitleLogo: (titleId) => request(`/titles/${titleId}/logo`, { method: 'DELETE' }),
  getTitleLogoUrl: (titleId) => `${API_BASE}/titles/${titleId}/logo`,
  fetchTitleLogo: async (titleId) => {
    const token = getToken();
    const res = await fetch(`${API_BASE}/titles/${titleId}/logo`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!res.ok) return null;
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },

  // Subscribers
  getSubscribers: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/subscribers${qs ? '?' + qs : ''}`);
  },
  getSubscriber: (id) => request(`/subscribers/${id}`),
  createSubscriber: (data) => request('/subscribers', { method: 'POST', body: data }),
  updateSubscriber: (id, data) => request(`/subscribers/${id}`, { method: 'PUT', body: data }),
  deleteSubscriber: (id) => request(`/subscribers/${id}`, { method: 'DELETE' }),
  // opts: { subscription: { title_id, start_date, end_date }, dry_run }
  importSubscribers: (subscribers, opts = {}) => request('/subscribers/import', { method: 'POST', body: { subscribers, ...opts } }),
  generateSubscriberLabels: (ids) => request(`/subscribers/labels?ids=${ids.join(',')}`),

  // Subscriptions
  getSubscriptions: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/subscriptions${qs ? '?' + qs : ''}`);
  },
  createSubscription: (data) => request('/subscriptions', { method: 'POST', body: data }),
  updateSubscription: (id, data) => request(`/subscriptions/${id}`, { method: 'PUT', body: data }),
  renewSubscription: (id, data) => request(`/subscriptions/${id}/renew`, { method: 'POST', body: data }),

  // Editions
  getEditions: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/editions${qs ? '?' + qs : ''}`);
  },
  createEdition: (data) => request('/editions', { method: 'POST', body: data }),
  updateEdition: (id, data) => request(`/editions/${id}`, { method: 'PUT', body: data }),
  deleteEdition: (id) => request(`/editions/${id}`, { method: 'DELETE' }),
  getLabelCount: (id) => request(`/editions/${id}/label-count`),
  generateLabels: async (id) => {
    const result = await request(`/editions/${id}/labels`);
    return result;
  },
  downloadTestLabel: async () => {
    const result = await request('/editions/labels/test');
    if (result.blob) {
      downloadBlob(result.blob, 'test-label.pdf');
    }
    return result;
  },
  getAnPostCount: (id) => request(`/editions/${id}/anpost-count`),
  generateAnPostCSV: async (id) => {
    const result = await request(`/editions/${id}/anpost-csv`);
    return result;
  },

  // Renewals
  getPendingRenewals: () => request('/renewals/pending'),
  sendRenewals: (subscription_ids, method) =>
    request('/renewals/send', { method: 'POST', body: { subscription_ids, method } }),
  getRenewalLog: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/renewals/log${qs ? '?' + qs : ''}`);
  },

  // Settings
  getSettings: () => request('/settings'),
  updateSettings: (data) => request('/settings', { method: 'PUT', body: data }),
};

export { downloadBlob };
export default api;
